import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactQuill from 'react-quill'
import { closeSnackbar, enqueueSnackbar } from 'notistack'
import { CustomEditorFooter, CustomEditorToolbar, StyledBox } from './components'
import { Button, Typography, useTheme } from '@mui/material'
import { DEFAULT_TEXT_LENGTH, TEXT_EDITOR_ATTRIBUTES } from '@app/constants'
import { copyText, cutText, editorFormats, moveFocusToNextElement, pasteText, reDo, unDo } from './utils'
import { SpecialCharactersModal } from '../../modals/SpecialCharactersModal'
import { TextFormatType } from '@app/types'

interface TextEditorComponentProps {
  value: string
  changeHandler: (text: string, xhtml: string, lines: number) => void
  placeholder: string
  isDisabled: boolean
  expectedLength?: number
  expectedLengthDisplay?: 'percentage' | 'count'
  format?: TextFormatType
}

export const TextEditorComponent: FC<TextEditorComponentProps> = props => {
  const theme = useTheme()
  const {
    value,
    changeHandler,
    placeholder,
    isDisabled,
    expectedLength = DEFAULT_TEXT_LENGTH,
    expectedLengthDisplay = 'count',
    format = TextFormatType.xhtml,
    ...accessibilityAttr
  } = props
  const isRichText = format === TextFormatType.xhtml
  const isPercentage = expectedLengthDisplay === 'percentage'
  const [currentLength, setCurrentLength] = useState(() => value ? value.replace(/<[^>]*>/g, '').length : 0)
  const remainingPercent = expectedLength > 0 ? Math.max(0, Math.ceil(((expectedLength - currentLength) / expectedLength) * 100)) : 0
  const isAtLimit = currentLength >= expectedLength
  const [openCharactersModal, setOpenCharactersModal] = useState<boolean>(false)
  const quillRef = useRef<ReactQuill>()
  // Prevents false paste warnings when navigating back to a page with existing text, which Quill treats as a paste-like insert
  const isPasteRef = useRef(false) // track user pasting for snackbar truncating limit warning

  const editorModules = useMemo(
    () => ({
      toolbar: isRichText
        ? {
            container: '#toolbar',
            handlers: {
              cut: () => cutText(quillRef.current?.getEditor()),
              copy: () => copyText(quillRef.current?.getEditor()),
              paste: () => { isPasteRef.current = true; pasteText(quillRef.current?.getEditor()) },
              undo: () => unDo(quillRef.current?.getEditor()),
              redo: () => reDo(quillRef.current?.getEditor()),
            },
          }
        : false,
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: () => moveFocusToNextElement(quillRef.current?.getEditor()),
          },
        },
      },
      history: {
        delay: 500,
        maxStack: 100,
      },
    }),
    [isRichText],
  )

  const handleOpenCharactersModal = () => {
    setOpenCharactersModal(true)
  }

  const handleCloseCharactersModal = () => {
    setOpenCharactersModal(false)
  }

  const insertSpecialCharacter = useCallback((character: string) => {
    const editor: any = quillRef.current?.getEditor()
    const cursorIndex = editor?.selection?.savedRange?.index
    editor.insertText(cursorIndex || 0, character)
  }, [])

  useEffect(() => {
    if (quillRef.current) {
      setCurrentLength(quillRef.current.getEditor().getLength() - 1)
    }
  }, [value])

  useEffect(() => {
    quillRef.current?.getEditor().enable(!isDisabled)
  }, [isDisabled])

  // Block keyboard input when at the character limit
  // Allow navigation, deletion, selection, and shortcuts
  useEffect(() => {
    const editor = quillRef.current?.getEditor()
    if (!editor) return

    const editorRoot = editor.root
    const handleKeyDown = (e: KeyboardEvent) => {
      const length = editor.getLength() - 1
      if (length < expectedLength) return

      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'Tab',
        'Escape',
      ]
      if (allowedKeys.includes(e.key)) return
      if (e.ctrlKey || e.metaKey) return
      if (e.key.length > 1) return

      e.preventDefault()
    }

    editorRoot.addEventListener('keydown', handleKeyDown)
    return () => editorRoot.removeEventListener('keydown', handleKeyDown)
  }, [expectedLength])

  useEffect(() => {
    quillRef.current?.getEditor().keyboard.addBinding(
      {
        key: 'V',
        shortKey: true,
      },
      () => { isPasteRef.current = true; pasteText(quillRef.current?.getEditor()) },
    )

    quillRef.current?.getEditor().keyboard.addBinding(
      {
        key: 'C',
        shortKey: true,
      },
      () => copyText(quillRef.current?.getEditor()),
    )

    quillRef.current.getEditor().root.setAttribute('spellcheck', 'false')
    setCurrentLength(quillRef.current.getEditor().getLength() - 1)
  }, [])

  return (
    <>
      <StyledBox className='text-editor' {...TEXT_EDITOR_ATTRIBUTES} {...accessibilityAttr}>
        {isPercentage && (
          <Typography
            variant='body2'
            sx={{ color: isAtLimit ? 'orange.700' : undefined, marginBottom: '0 !important', paddingBottom: 2 }}
          >
            {`Space remaining: ${remainingPercent}%`}
          </Typography>
        )}
        {isRichText && <CustomEditorToolbar disabled={isDisabled} handleOpenCharactersModal={handleOpenCharactersModal} />}
        <ReactQuill
          theme='snow'
          modules={editorModules}
          formats={isRichText ? editorFormats : []}
          value={value}
          tabIndex={0}
          onChange={(_value, _delta, _source, editor) => {
            if (editor.getLength() > expectedLength) {
              const textLength = editor.getLength() - 1
              const willTruncate = textLength > expectedLength
              if (willTruncate && isPasteRef.current) {
                enqueueSnackbar('Pasted text was truncated. Please check your answer before submission.', {
                  variant: 'warning',
                  autoHideDuration: 5000,
                  style: { backgroundColor: theme.palette.orange['700'] },
                  action: (snackbarId) => (
                    <Button size="small" sx={{ color: 'white' }} onClick={() => closeSnackbar(snackbarId)}>
                      Dismiss
                    </Button>
                  ),
                })
              }
              isPasteRef.current = false
              quillRef.current?.getEditor().deleteText(expectedLength, editor.getLength())
            }
            const text = editor.getText(0, editor.getLength())
            const html = text.trim() ? editor.getHTML() : ''
            setCurrentLength(editor.getLength() - 1)
            changeHandler(text, html, text.split('\n').length - 1)
          }}
          placeholder={placeholder}
          ref={quillRef}
        />
        <CustomEditorFooter
          textLength={currentLength}
          maxTextLength={expectedLength}
          displayStyle={expectedLengthDisplay}
          isAtLimit={isAtLimit}
        />
      </StyledBox>

      <SpecialCharactersModal
        open={openCharactersModal}
        onClose={handleCloseCharactersModal}
        insertSpecialCharacter={insertSpecialCharacter}
      />
    </>
  )
}
